import {Component} from 'vue-property-decorator';
import {DEFAULT_LABEL_WIDTH} from '@/utils/Constants';
import store from '@/store';
import {CommonModelUtils, CommonReasonDialogResponse, DiscardConfirmDialogResponse} from '@/model/CommonModel';
import DefaultViewMixin from '@/views/DefaultViewMixin';
import MessageUtils from '@/utils/MessageUtils';
import AppSession from '@/store/AppSession';
import ApiUtils from '@/utils/ApiUtils';

/**
 * 모든 상세 화면이 적용 해야 할 Mixin
 *
 * label width, align, required에 대한 내용을 정의하고 있으며
 * form의 변경감지 및 validation에 대한 공통 처리를 수행한다.
 *
 * @see DefaultViewMixin
 */
@Component
export default class DefaultFormMixin extends DefaultViewMixin {
    protected formDisabled: boolean = false;
    protected DEFAULT_LABEL_WIDTH = `${DEFAULT_LABEL_WIDTH}px`;
    
    get isLoading() {
        return this.loading;
    }
    
    get isFormDisabled() {
        return this.formDisabled;
    }
    
    get getSavebuttonClass(): string {
        const changed = Object.assign({}, store.state.valueChangedMap,
            store.state.notationChangedMap, store.state.nullFlavorChangedMap);
    
        return Object.keys(changed).length > 0 ? 'icon-button-save-primary' : 'icon-button-save';
    }
    
    get isFormReadonly() {
        return !this.hasEditPrivilege || this.$store.getters.isFormDeleted;
    }
    
    get getTableRowClass() {
        return (row: any) => {
            return {
                'delete-row': row.delFlag === 'Y' || row.deleted === true || row.personDeleteFlag === 'Y' ||
                    row.ITEM_DEL_FLAG === 'Y' || row.CODE_DEL_FLAG === 'Y' || row.DEL_FLAG === 'Y'
            };
        };
    }
    
    get deleteIcon() {
        return (deleted: boolean | string) => {
            return !CommonModelUtils.getBoolean(deleted) ? 'r3-icon-delete' : 'r3-icon-restore';
        };
    }
    
    get isTableRowItemReadonly() {
        return (row: any) => {
            return (row.delFlag === 'Y' || row.DEL_FLAG === 'Y' || row.deleted === true || row.personDeleteFlag === 'Y' ||
                row.ITEM_DEL_FLAG === 'Y' || row.CODE_DEL_FLAG === 'Y')
                || this.isFormReadonly;
        };
    }
    
    public getLabelClass(isRequired: boolean = false, align: string = 'left', additional: string = '') {
        return `${isRequired ? 'is-required' : ''} ${align} ${additional}`;
    }
    
    public getRequiredHeader(label: string) {
        return `<em class="required"></em> ${label}`;
    }
    
    public validation(onSuccess: () => void, onFail: (result: any) => void = () => {
    }) {
        const $refFrom: any = this.$refs.form;
        $refFrom.validate((valid: boolean, result: any) => {
            if (valid) {
                onSuccess();
            } else {
                this.focusToFirstElement(result);
                onFail(result);
            }
        });
    }
    
    public validateField(prop: string) {
        const $refFrom: any = this.$refs.form;
        $refFrom.validateField(prop);
    }
    
    private focusToFirstElement(result: any) {
        if (!result) {
            return;
        }
        
        const first: any = result[Object.keys(result)[0]];
        if (!first) {
            return;
        }
        
        const error: any = first[0];
        if (!error) {
            return;
        }
    
        const ref: any = this.$refs[error.field];
        if (ref && ref.focus instanceof Function) {
            ref.focus();
        }
    }
    
    public resetFields() {
        const $refFrom: any = this.$refs.form;
        if ($refFrom && $refFrom.resetFields instanceof Function) {
            $refFrom.resetFields();
        }
    }
    
    public clearValidate(prop: string) {
        const $refFrom: any = this.$refs.form;
        if ($refFrom && $refFrom.clearValidate instanceof Function) {
            $refFrom.clearValidate(prop);
        }
    }
    
    public onSwitchDelete(deleted: any) {
        this.$store.commit('setFormStatus', {
            deleted: CommonModelUtils.getBoolean(deleted)
        });
    }
    
    /**
     * form에 대한 변경 사항을 감지하며 아래 Promise를 반환한다
     *  - resolve: 변경 사항이 없거나 Discard confirm dialog에서 confirm event가 떨어졌을 때
     *  - reject: Discard confirm dialog에서 cancel event가 떨어졌을 때
     *
     * @param active        (optional) 새롭게 active 될 view name
     * @param oldActive     (optional) 현재 view name
     */
    protected onBeforeLeave(active?: string, oldActive?: string) {
        const changed = Object.assign({}, store.state.valueChangedMap,
            store.state.notationChangedMap, store.state.nullFlavorChangedMap);

        return new Promise<any>((resolve, reject) => {
            if (Object.keys(changed).length > 0) {
                this.$modal.showDiscardConfirmDialog({
                    onConfirm: (res: DiscardConfirmDialogResponse) => {
                        if (res.result) {
                            this.$store.commit('initChangeMap');
                            resolve();
                        } else {
                            reject();
                        }
                    }
                });
            } else {
                resolve();
            }
        });
    }

    protected getReasonDialogPromise(noModifyMessage: string = this.$t('message.commonReasonDialog.noModifyData').toString())
        : Promise<CommonReasonDialogResponse> {

        this.$store.commit('extendSessionTime');
        return new Promise(((resolve, reject) => {
            if (this.$store.getters.hasDateError) {
                this.$store.commit('setDateError', false);
                return;
            }
            if (this.isFormChanged()) {
                MessageUtils.clear();
                this.$modal.showReasonDialog({
                    onConfirm: (res: CommonReasonDialogResponse) => {
                        if (res.result) {
                            resolve(res);
                        }
                    }
                });
            } else {
                reject(noModifyMessage);
            }
        }));
    }
    
    protected isFormChanged(): boolean {
        const changed = Object.assign({}, store.state.valueChangedMap,
            store.state.notationChangedMap, store.state.nullFlavorChangedMap);
        return Object.keys(changed).length > 0;
    }
    
    /**
     * Submit action이 성공 했을 때 공통으로 처리하는 핸들러
     *
     * - 화면에 띄워진 메시지를 모두 닫고 성공 메시지를 띄운다
     * - Store에 커밋 된 Change map을 비우며 loading spinner를 off 한다
     *
     * @param message Success message (default = 'message.success')
     */
    protected afterSubmitSuccess(message: string = this.$t('message.success').toString()) {
        MessageUtils.clear();
        MessageUtils.displaySuccessMessage(message);
        
        return new Promise(resolve => {
            setTimeout(() => {
                this.loading = false;
                this.formDisabled = false;
                this.$store.commit('initChangeMap');
                this.$store.commit('setCaseFormDisabled', false);
                resolve();
            }, 300);
        });
    }
    
    /**
     * Submit action이 실패 했을 때 공통으로 처리하는 핸들러
     *
     * - 화면에 띄워진 메시지를 모두 닫고 실패 메시지를 띄운다
     * - loading spinner를 off 한다
     *
     * @param message Failure message
     * @param body Failure body
     */
    protected afterSubmitFail(message: string, body: any = {}) {
        MessageUtils.clear();
        this.loading = false;
        this.formDisabled = false;
        this.$store.commit('setCaseFormDisabled', false);
        const obj = {
            userId: this.$store.getters.userId,
            userKey: this.$store.getters.userKey,
            token: AppSession.getAccessToken(),
            message,
            body,
            route: {
                fullPath: this.$route.fullPath,
                path: this.$route.path,
                name: this.$route.name,
                params: this.$route.params,
                query: this.$route.query
            },
            additional: {
                message,
                category: 'SUBMIT_FAIL'
            }
        };
        ApiUtils.reportError('SUBMIT_FAIL', obj);
        return MessageUtils.displayErrorMessage(message);
    }
}
